// First-party product analytics. No vendor, no third-party script, no cookie
// banner — events go straight into our own Supabase table (public.product_events,
// FIX-DATABASE-24) and only we can read them.
//
// Why this exists: before it, we had ZERO visibility into the funnel. We could
// not answer "how many people hit the landing page", "how many signed up",
// "how many ever created a real job", or "how many saw the paywall and left".
// Every one of those is a decision we were making blind.
//
// Rules this file lives by:
//   1. Analytics never blocks or breaks the app. Every path is try/catch'd and
//      fire-and-forget — if the insert fails, the user never knows.
//   2. Everything is sanitized + capped to match the DB CHECK constraints, so a
//      hand-edited call site can't stuff garbage into the table.
//   3. No PII in props. Ever. Names, emails, addresses and dollar amounts stay
//      out — we log SHAPE (did it happen, on which screen), not content.

import { supabase } from '../supabaseClient'

const AID_KEY = 'jobtally_aid'

// Canonical event names. Using the constants instead of raw strings keeps the
// funnel from silently splintering into 'signup' / 'signUp' / 'signup_done'.
export const EV = {
  LANDING_VIEW: 'landing_view',
  LANDING_CTA: 'landing_cta',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  APP_OPENED: 'app_opened',
  SAMPLE_JOB_SEEDED: 'sample_job_seeded',
  SAMPLE_JOB_DISMISSED: 'sample_job_dismissed',
  JOB_CREATED: 'job_created',
  RECEIPT_ADDED: 'receipt_added',
  TIME_ADDED: 'time_added',
  WORKER_INVITED: 'worker_invited',
  JOB_COMPLETED: 'job_completed',
  PAYWALL_HIT: 'paywall_hit',
  CHECKOUT_STARTED: 'checkout_started',
  TESTIMONIAL_PROMPTED: 'testimonial_prompted',
  TESTIMONIAL_SUBMITTED: 'testimonial_submitted',
  TESTIMONIAL_DISMISSED: 'testimonial_dismissed',
  LEAD_SUBMITTED: 'lead_submitted',
  LEAD_FAILED: 'lead_failed',
}

// Same sanitizer contract as utils/attribution.js: strip control chars,
// collapse whitespace, cap length.
const clean = (v, max = 64) =>
  Array.from(String(v || ''))
    .filter((ch) => ch.charCodeAt(0) >= 32 && ch.charCodeAt(0) !== 127)
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max)

// A stable random id for this browser, so a landing-page view by a logged-out
// stranger can be joined to the account it eventually produced. Generated by
// us and stored in localStorage — it is NOT a fingerprint and carries nothing
// about the person.
function anonId() {
  try {
    let id = localStorage.getItem(AID_KEY)
    if (!id) {
      id =
        typeof crypto !== 'undefined' && crypto.randomUUID
          ? crypto.randomUUID()
          : `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem(AID_KEY, id)
    }
    return clean(id)
  } catch {
    return null // private mode / storage blocked — event still records, just unjoined
  }
}

// Set once auth resolves so subsequent events are attached to the account.
// Kept in module state rather than re-reading the session on every call, which
// would make a fire-and-forget event await the auth lock.
let currentUserId = null
export function setAnalyticsUser(userId) {
  currentUserId = userId || null
}

// Props are a small bag of context. Only primitives survive, keys and values
// are capped, and the whole object is capped at 12 keys — the DB rejects
// anything over 2KB and we'd rather truncate here than lose the event.
function cleanProps(props) {
  const out = {}
  if (!props || typeof props !== 'object') return out
  let n = 0
  for (const [k, v] of Object.entries(props)) {
    if (n >= 12) break
    if (v === null || v === undefined) continue
    const key = clean(k, 32)
    if (!key) continue
    if (typeof v === 'number' && Number.isFinite(v)) out[key] = v
    else if (typeof v === 'boolean') out[key] = v
    else out[key] = clean(v, 96)
    n++
  }
  return out
}

// Fire-and-forget. Deliberately NOT awaited at call sites — an event must never
// sit in front of a user action.
export function track(event, props) {
  try {
    const name = clean(event, 64)
    if (!name) return
    // Not awaited: the promise is handled here so a rejection can't escape as
    // an unhandled rejection into the global handler in App.js.
    supabase
      .from('product_events')
      .insert({
        user_id: currentUserId || null,
        anon_id: anonId(),
        event: name,
        props: cleanProps(props),
      })
      .then(
        () => {},
        () => {} // offline, RLS, table not migrated yet — all non-events
      )
  } catch {
    // Analytics is never allowed to throw into the app.
  }
}

// Same as track(), but at most once per browser tab. For things that render
// repeatedly (a paywall screen, a landing view) where 40 duplicate rows per
// visit would just be noise.
const fired = new Set()
export function trackOnce(event, props) {
  const name = clean(event, 64)
  if (!name || fired.has(name)) return
  fired.add(name)
  track(name, props)
}

// Milestone events we only ever want ONCE per account, for real (not per tab):
// "this owner created their first job". Backed by localStorage so a page
// refresh doesn't re-fire it. Best-effort by design — worst case we get a
// duplicate, which the funnel counts with count(distinct user_id) anyway.
export function trackMilestone(event, props) {
  try {
    const name = clean(event, 64)
    if (!name) return
    const key = `jobtally_ms_${name}_${currentUserId || 'anon'}`
    if (localStorage.getItem(key)) return
    localStorage.setItem(key, '1')
    track(name, props)
  } catch {
    track(event, props)
  }
}
